// Files in and out: the count file (a track's events, exchange.ts), the truth file, and the song and count pickers.

import type { CountEvent } from '@salsacount/core';
import { buildAllExport, buildExport, parseExport, planImport } from '../exchange.js';
import { engine } from '../engine/engine.js';
import { readAllRecords, readRecords } from '../engine/log.js';
import { listSongs } from './library.js';
import { announce, showToast } from './ui.js';

export function download(name: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const stem = (name: string): string => name.replace(/\.[^./\\]+$/, '');

/** Save the truth taps of the open song (what the benchmark reads). */
export async function exportTruth(): Promise<void> {
  const doc = await engine.truthDocument();
  const track = engine.getState().track;
  if (doc && track) download(`${stem(track.name)}.truth.json`, JSON.stringify(doc, null, 2));
}

/** Save the count of the open song: every tap and fix, in order, with the calibration used and the tracker's version (exchange.ts). */
export async function exportLog(): Promise<void> {
  const { track, events, calibrationOffsetS } = engine.getState();
  if (!track || engine.getState().source !== 'file') return;
  // The stored records carry when each event was made; if storage is refused the session's own events are written without them.
  let records: { event: CountEvent; wall: number; offsetS: number; device?: string }[];
  try {
    const stored = await readRecords(track.id);
    records = stored.length >= events.length ? stored : events.map((event) => ({ event, wall: Date.now(), offsetS: calibrationOffsetS ?? 0, device: engine.deviceKey }));
  } catch {
    records = events.map((event) => ({ event, wall: Date.now(), offsetS: calibrationOffsetS ?? 0, device: engine.deviceKey }));
  }
  const calibration = calibrationOffsetS === null ? null : { offsetS: calibrationOffsetS, device: engine.deviceKey };
  const doc = buildExport({ track, records, calibration, trackerVersion: engine.trackerInfo.version });
  download(`${stem(track.name)}.count.json`, JSON.stringify(doc, null, 2));
  announce('Count saved to a file');
}

/**
 * Read a count file back in and merge it into this song's log (exchange.ts): events this device does not have are appended, none is
 * dropped or rewritten, and a file made for a different song is refused with a sentence.
 */
export async function importLog(file: File): Promise<void> {
  const { track } = engine.getState();
  if (!track || engine.getState().source !== 'file') return;
  let text: string;
  try {
    text = await file.text();
  } catch {
    showToast('That file could not be read. Choose a file made with Export count.');
    return;
  }
  const parsed = parseExport(text);
  if (!parsed.ok) { showToast(parsed.sentence); return; }
  const plan = planImport(track.id, engine.getState().events, parsed.doc);
  if (plan.kind === 'refused') { showToast(plan.sentence); return; }
  if (plan.plan.add.length > 0) engine.importEvents(plan.plan.add, plan.plan.meta);
  showToast(plan.sentence);
}

// The two hidden file inputs live at the root of the app (pickers.tsx) and are opened from here, so a sheet can close right after the click.
let songInput: HTMLInputElement | null = null;
let logInput: HTMLInputElement | null = null;

export function registerPickers(song: HTMLInputElement | null, log: HTMLInputElement | null): void {
  songInput = song;
  logInput = log;
}

export function pickSong(): void {
  songInput?.click();
}

export function pickLog(): void {
  logInput?.click();
}

/**
 * Export all my counts (S9e): one file with every song stored on this device (its name, hash and duration, and its whole event log; no audio), so the answers given to the automatic count
 * can be read as labels for "1" (tools/bench/counts_truth.ts). Songs the library no longer lists but whose logs are stored are included, with no name.
 */
export async function exportAllCounts(): Promise<void> {
  let by: Awaited<ReturnType<typeof readAllRecords>>;
  try {
    by = await readAllRecords();
  } catch {
    showToast('Your counts could not be read from this browser. Open each song and use Export count.');
    return;
  }
  const library = await listSongs();
  const known = new Map(library.map((s) => [s.id, s]));
  const ids = [...library.map((s) => s.id).filter((id) => by.has(id)), ...[...by.keys()].filter((id) => !known.has(id))];
  const songs = ids.map((id) => ({ id, name: known.get(id)?.name ?? null, duration: known.get(id)?.duration ?? null, records: by.get(id) ?? [] }));
  const doc = buildAllExport({ songs });
  download(`salsacount-counts-${doc.exportedAt.slice(0, 10)}.json`, JSON.stringify(doc, null, 2));
  announce(songs.length === 0 ? 'No counts to export yet' : `${songs.length} ${songs.length === 1 ? 'song' : 'songs'} saved to a file`);
  if (songs.length === 0) showToast('No counts to export yet. Count a song first.');
}
